/**
 * Client for the 8-week Bootcamp program API.
 *
 * Every function matches one backend endpoint under `/education/bootcamp/`.
 * Types mirror the DRF serializer shapes one-to-one — update both sides
 * together if you ever change the schema.
 */

import { apiClient } from './apiClient';

// ── Types ────────────────────────────────────────────────────────────────

export type DeliverableSchemaItem = {
    key: string;
    label: string;
    type: 'text' | 'url' | 'file' | 'list';
    description?: string;
    required?: boolean;
};

export type BootcampLessonMini = {
    id: string;
    title: string;
    duration: string;
    has_quiz: boolean;
    order: number;
};

export type BootcampQuizMini = {
    id: string;
    title: string;
    description: string;
    difficulty: 'Easy' | 'Medium' | 'Hard';
    time_limit: number;
    passing_score: number;
    question_count: number;
};

export type BootcampWeekProgress = {
    id: string;
    unlocked: boolean;
    unlocked_at: string | null;
    /** Earliest moment this week CAN unlock (cadence gate). null before
     *  the benchmark is submitted. */
    available_from: string | null;
    /** Convenience: seconds remaining until ``available_from``. 0 when
     *  already in the past. null when the week is already unlocked. */
    seconds_until_available: number | null;
    prep_completed: boolean;
    prep_completed_at: string | null;
    deliverables_submitted: boolean;
    deliverables_submitted_at: string | null;
    pass_status: 'pending' | 'submitted' | 'pass' | 'retry';
    reviewer_notes: string;
    reviewed_at: string | null;
    deliverables: Record<string, unknown>;
};

export type BootcampWeekSummary = {
    id: string;
    week_number: number;
    theme: string;
    objectives: string[];
    activities: string[];
    deliverable_summary: string;
    deliverables_schema: DeliverableSchemaItem[];
    unlock_policy: 'sequential' | 'open';
    estimated_hours: number;
    course_id: string;
    course_title: string;
    course_description: string;
    thumbnail: string;
    lessons_count: number;
    progress: BootcampWeekProgress | null;
};

export type BootcampWeekDetail = BootcampWeekSummary & {
    lessons: BootcampLessonMini[];
    quizzes: BootcampQuizMini[];
};

export type BootcampEnrollment = {
    id: string;
    program: string;
    status: 'benchmarking' | 'active' | 'completed' | 'withdrawn';
    cohort: string;
    benchmark_submitted_at: string | null;
    evaluation_submitted_at: string | null;
    started_at: string;
    completed_at: string | null;
};

export type BootcampProgram = {
    id: string;
    slug: string;
    title: string;
    tagline: string;
    mission: string;
    week_count: number;
    weeks: BootcampWeekSummary[];
    enrollment: BootcampEnrollment | null;
};

export type BenchmarkPayload = {
    experience_level: 'none' | 'hobbyist' | 'graduate' | 'junior' | 'mid' | 'senior';
    years_in_industry: number;
    practice_commitment: 'daily' | 'often' | 'regular' | 'occasional' | 'rarely';
    self_rating_technical: number;
    self_rating_communication: number;
    self_rating_leadership: number;
    background: string;
    goals: string;
    strengths: string;
    weaknesses: string;
    preferred_learning: 'visual' | 'auditory' | 'kinesthetic' | 'reading';
    /** Optional role-model pick (e.g. "Angela Foster" from the case studies). */
    role_model?: string;
};

// ── Quiz types (shared with courseService but re-exported here so the
// bootcamp surface doesn't need a cross-import) ─────────────────────────

export type BootcampQuizQuestion = {
    id: string;
    type: 'mcq' | 'text';
    question: string;
    options?: string[];
    correct_answer: string;
    explanation: string;
};

export type BootcampQuizFull = {
    id: string;
    title: string;
    description: string;
    difficulty: 'Easy' | 'Medium' | 'Hard';
    time_limit: number;
    passing_score: number;
    questions: BootcampQuizQuestion[];
};

export type BootcampQuizAttemptResult = {
    attempt_id: string;
    score: number;
    passed: boolean;
    passing_score: number;
    answers_review: Array<{
        question_id: string;
        question: string;
        user_answer: string;
        correct_answer: string;
        is_correct: boolean;
        explanation: string;
    }>;
};

export type BootcampQuizAttempt = {
    id: string;
    quiz: string;
    quiz_title: string;
    answers: Record<string, string>;
    score: number;
    passed: boolean;
    started_at: string;
    completed_at: string | null;
};

export type EvaluationPayload = {
    self_rating_technical: number;
    self_rating_communication: number;
    self_rating_leadership: number;
    highlights: string;
    challenges: string;
    next_12_months: string;
};

// ── Endpoints ────────────────────────────────────────────────────────────

export async function getBootcampProgram(): Promise<BootcampProgram> {
    const resp = await apiClient.get('/education/bootcamp/');
    return resp.data as BootcampProgram;
}

export async function enrollInBootcamp(cohort = ''): Promise<BootcampEnrollment> {
    const resp = await apiClient.post('/education/bootcamp/enroll/', { cohort });
    return resp.data as BootcampEnrollment;
}

export async function withdrawFromBootcamp(reason = ''): Promise<BootcampEnrollment> {
    const resp = await apiClient.post('/education/bootcamp/withdraw/', { reason });
    return resp.data as BootcampEnrollment;
}

export async function submitBenchmark(payload: BenchmarkPayload): Promise<BootcampEnrollment> {
    const resp = await apiClient.post('/education/bootcamp/benchmark/', payload);
    return resp.data as BootcampEnrollment;
}

export async function getBootcampWeekDetail(weekNumber: number): Promise<BootcampWeekDetail> {
    const resp = await apiClient.get(`/education/bootcamp/weeks/${weekNumber}/`);
    return resp.data as BootcampWeekDetail;
}

export async function markPrepComplete(weekNumber: number): Promise<void> {
    await apiClient.post(`/education/bootcamp/weeks/${weekNumber}/prep/`, {});
}

export async function submitWeekDeliverables(
    weekNumber: number,
    deliverables: Record<string, unknown>,
): Promise<void> {
    await apiClient.post(`/education/bootcamp/weeks/${weekNumber}/deliverables/`, deliverables);
}

export async function submitEvaluation(payload: EvaluationPayload): Promise<BootcampEnrollment> {
    const resp = await apiClient.post('/education/bootcamp/evaluation/', payload);
    return resp.data as BootcampEnrollment;
}

// ── Quiz (uses the existing education quiz endpoints) ──────────────────

export async function getQuizDetail(quizId: string): Promise<BootcampQuizFull> {
    const resp = await apiClient.get(`/education/quizzes/${quizId}/`);
    return resp.data as BootcampQuizFull;
}

export async function submitQuizAttempt(
    quizId: string,
    answers: Record<string, string>,
): Promise<BootcampQuizAttemptResult> {
    const resp = await apiClient.post(`/education/quizzes/${quizId}/submit/`, { answers });
    return resp.data as BootcampQuizAttemptResult;
}

export async function getQuizAttempts(quizId: string): Promise<BootcampQuizAttempt[]> {
    const resp = await apiClient.get(`/education/quizzes/${quizId}/attempts/`);
    return Array.isArray(resp.data) ? (resp.data as BootcampQuizAttempt[]) : [];
}

// ── Admin / facilitator ────────────────────────────────────────────────

export type BootcampReviewQueueItem = {
    progress_id: string;
    enrollment_id: string;
    user_id: number;
    user_display: string;
    week_number: number;
    course_title: string;
    deliverables_submitted_at: string | null;
    pass_status: 'pending' | 'submitted' | 'pass' | 'retry';
};

export async function getReviewQueue(): Promise<BootcampReviewQueueItem[]> {
    const resp = await apiClient.get('/education/bootcamp/admin/review_queue/');
    return Array.isArray(resp.data) ? (resp.data as BootcampReviewQueueItem[]) : [];
}

export async function reviewDeliverables(
    enrollmentId: string,
    weekNumber: number,
    decision: 'pass' | 'retry',
    notes = '',
): Promise<{ progress_id: string; pass_status: string; reviewed_at: string }> {
    const resp = await apiClient.post(
        `/education/bootcamp/admin/reviews/${enrollmentId}/${weekNumber}/`,
        { decision, notes },
    );
    return resp.data;
}

// ── Final evaluation ───────────────────────────────────────────────────

// EvaluationPayload is already declared earlier in this file.

